import { ISimplifiedProjectView } from '../../api-client/autogenerated';

/**
 * My Work fetches its documents through the document authorization path, which is wider than the
 * project list the portal loads: it can return work sitting in projects the user cannot open —
 * archived or hidden ones, or ones they only touch as a document's creator/assignee without being
 * an actual member. Those rows have no project to name in the table's Project column and nowhere
 * to navigate to, so the table has always dropped them.
 *
 * Every part of the page has to agree on that set or the page contradicts itself. It previously
 * did not: the "By Status" doughnut's total and the table's page count were derived from the raw
 * fetch, while the "By Project" doughnut and the table body were derived from this narrower set —
 * so the two doughnuts reported different totals and pagination offered pages the body could never
 * fill. Resolving it here, once, is what keeps them consistent.
 *
 * Hidden/archived are treated as flags that must be positively set, matching how `getProjectsState`
 * itself filters (`!p.isArchived`): both columns are nullable, and the two call sites used to
 * disagree on whether a null meant "visible" (the chart's truthy test) or "not visible" (the
 * table's `=== false` test), which silently dropped such rows from the table alone.
 *
 * The archived half is belt and braces -- `getProjectsState` already drops archived projects, and
 * the API's My Work endpoints exclude their documents -- but it is what keeps My Work correct if a
 * caller ever passes the unfiltered list. An archived project is view-only, so it holds no work.
 */
export const getVisibleProjectIds = (projects: ISimplifiedProjectView[]): Set<string> =>
  new Set(projects.filter((p) => !p.isHidden && !p.isArchived).map((p) => p.id));

/**
 * The subset of `documents` whose project the user can actually open — see above. Generic over the
 * document shape so callers can pass rows they have already decorated (e.g. with `project`).
 */
export const filterDocumentsWithVisibleProject = <T extends { projectId: string }>(
  documents: T[],
  projects: ISimplifiedProjectView[],
): T[] => {
  const visibleProjectIds = getVisibleProjectIds(projects);
  return documents.filter((doc) => visibleProjectIds.has(doc.projectId));
};
